import * as consts from 'redux/constants';
import { CARD_FORM_VIEW } from 'consts/card';
import { produce } from 'immer';
import { get } from 'lodash-es';

const initialCardValues = {
  requiredFields: {
    mandatory: {},
    address: {},
    fields: {}
  },
  pmOptions: {}
};
const initialCardMeta = {
  saveForFuture: false,
  useMainAddress: true,
  submitForm: false,
  submitEditForm: false,
  editFormPristine: true,
  status: consts.REQ_STATUSES.IDLE,
  alertWasShown: false
};

const clearCardError = field => {
  field.error = '';
};
const setCardTouched = field => {
  field.touched = true;
};
const clearCardTouched = field => {
  field.touched = false;
};

const initialState = {
  cardSettings: {
    values: initialCardValues,
    meta: initialCardMeta,
    view: CARD_FORM_VIEW.LIST,
    selectedCard: {},
    cardToPay: {}
  },
  selectedType: {},
  selectedCategory: null,
  selectedCurrency: null,
  requiredFields: {},
  pmOptions: {}
};

const cardFormReducer = (state = initialState, action) => {
  switch (action.type) {
    case consts.SET_CARD_TO_PAY: {
      const { card } = action.payload;
      const newState = produce(state, ({ cardSettings }) => {
        cardSettings.cardToPay = card;
      });
      return newState;
    }
    case consts.INIT_CARD_FORM: {
      const { form } = action.payload;
      const newState = produce(state, ({ cardSettings }) => {
        cardSettings.values.requiredFields.mandatory = form.mandatory;
        cardSettings.values.requiredFields.address = form.address;
        cardSettings.meta.saveForFuture = form.saveForFuture;
      });
      return newState;
    }
    case consts.INIT_CARD_FORM_ADDRESS: {
      const { formAddress } = action.payload;
      const newState = produce(state, ({ cardSettings }) => {
        cardSettings.values.requiredFields.address = formAddress;
      });
      return newState;
    }
    case consts.RESET_CARD_FORM: {
      const newState = produce(state, ({ cardSettings }) => {
        cardSettings.values = initialCardValues;
        cardSettings.meta = initialCardMeta;
        cardSettings.view = CARD_FORM_VIEW.LIST;
      });
      return newState;
    }
    case consts.UPDATE_CARD_FORM: {
      const { pmtAdditionalFields } = action.payload;
      const fields = Object.values(pmtAdditionalFields).reduce((acc, current) => {
        if (current.location === 'fields')
          return {
            ...acc,
            [current.name]: current
          };
        return acc;
      }, {});
      const pmOptions = Object.values(pmtAdditionalFields).reduce((acc, current) => {
        if (current.location === 'payment_method_options')
          return {
            ...acc,
            [current.name]: current
          };
        return acc;
      }, {});
      const newState = produce(state, ({ cardSettings }) => {
        cardSettings.values.requiredFields.fields = fields;
        cardSettings.values.pmOptions = pmOptions;
      });
      return newState;
    }
    case consts.ON_CHANGE_CARD_FORM: {
      const { fieldPath, value, isCheckbox } = action.payload;
      const newState = produce(state, ({ cardSettings }) => {
        if (isCheckbox) {
          cardSettings.meta[fieldPath] = value;
          cardSettings.meta.useMainAddress = value;
        } else {
          const fieldToUpdate = get(cardSettings.values, fieldPath);
          if (fieldToUpdate) {
            fieldToUpdate.value = value;
            fieldToUpdate.touched = true;
          }
        }
      });
      return newState;
    }
    case consts.SET_CARD_FORM_ERROR: {
      const { fieldPath, errorMessage } = action.payload;
      const newState = produce(state, ({ cardSettings }) => {
        const { submitForm: isSubmittingForm } = state.cardSettings.meta;
        const fieldToUpdate = get(cardSettings.values, fieldPath);
        if (fieldToUpdate) {
          const alreadyTouched = fieldToUpdate?.touched;
          fieldToUpdate.error = errorMessage;
          fieldToUpdate.touched =
            isSubmittingForm || alreadyTouched || fieldToUpdate?.value?.length >= 1;
        }
      });
      return newState;
    }
    case consts.SET_SUBMIT_EDIT_FORM:
    case consts.SET_SUBMIT_CARD_FORM: {
      const { submit, isEdit } = action.payload;
      const newState = produce(state, ({ cardSettings }) => {
        const { meta } = cardSettings;
        if (isEdit) {
          meta.submitEditForm = submit;
        } else {
          meta.submitForm = submit;
        }
      });
      return newState;
    }
    case consts.SET_SELECTED_CARD: {
      const { card } = action.payload;
      const newState = produce(state, ({ cardSettings }) => {
        cardSettings.selectedCard = card;
      });
      return newState;
    }
    case consts.CLEAR_CARD_FORM_ERRORS: {
      const { fieldsPath } = action.payload;
      const newState = produce(state, ({ cardSettings }) => {
        const fieldToUpdate = get(cardSettings.values, fieldsPath);
        Object.values(fieldToUpdate).forEach(clearCardError);
      });
      return newState;
    }
    case consts.CLEAR_ALL_CARD_FORM_ERRORS: {
      const newState = produce(state, ({ cardSettings }) => {
        Object.values(cardSettings.values.requiredFields.mandatory).forEach(
          clearCardError
        );
        Object.values(cardSettings.values.requiredFields.address).forEach(clearCardError);
        Object.values(cardSettings.values.requiredFields.fields).forEach(clearCardError);
        Object.values(cardSettings.values.pmOptions).forEach(clearCardError);
      });
      return newState;
    }
    case consts.CLEAR_ALL_CARD_FORM_TOUCHED: {
      const newState = produce(state, ({ cardSettings }) => {
        Object.values(cardSettings.values.requiredFields.mandatory).forEach(
          clearCardTouched
        );
        Object.values(cardSettings.values.requiredFields.address).forEach(
          clearCardTouched
        );
        Object.values(cardSettings.values.requiredFields.fields).forEach(
          clearCardTouched
        );
        Object.values(cardSettings.values.pmOptions).forEach(clearCardTouched);
      });
      return newState;
    }
    case consts.CLEAR_CUSTOMER_ADDRESS_TOUCHED: {
      const newState = produce(state, ({ cardSettings }) => {
        Object.values(cardSettings.values.requiredFields.address).forEach(
          clearCardTouched
        );
      });
      return newState;
    }
    case consts.TOUCH_ALL: {
      const newState = produce(state, ({ cardSettings }) => {
        Object.values(cardSettings.values.requiredFields.mandatory).forEach(
          setCardTouched
        );
        Object.values(cardSettings.values.requiredFields.address).forEach(setCardTouched);
        Object.values(cardSettings.values.requiredFields.fields).forEach(setCardTouched);
        Object.values(cardSettings.values.pmOptions).forEach(setCardTouched);
      });
      return newState;
    }
    case consts.TOUCH_ADDRESS: {
      const newState = produce(state, ({ cardSettings }) => {
        Object.values(cardSettings.values.requiredFields.address).forEach(setCardTouched);
      });
      return newState;
    }
    case consts.SET_CARD_FORM_VIEW: {
      const { view } = action.payload;
      const newState = produce(state, ({ cardSettings }) => {
        cardSettings.view = view;
      });
      return newState;
    }
    case consts.UPDATE_EDIT_PRISTINE: {
      const { pristine } = action.payload;
      const newState = produce(state, ({ cardSettings }) => {
        cardSettings.meta.editFormPristine = pristine;
      });
      return newState;
    }
    case consts.ALERT_WAS_SHOWN: {
      const newState = produce(state, ({ cardSettings }) => {
        cardSettings.meta.alertWasShown = true;
      });
      return newState;
    }

    default:
      return state;
  }
};

export default cardFormReducer;
